import WebTileLayer from '@arcgis/core/layers/WebTileLayer';
import ArcGISBasemap from '@arcgis/core/Basemap';
import MapView from '@arcgis/core/views/MapView';
import GoogleAttributionBuilder, { GoogleAttribution } from './google-attribution';

export interface GoogleBasemap {
  buildBasemap: (mapType: string, mapView: MapView) => Promise<ArcGISBasemap>;
}

interface CachedSessions {
  id: string;
  type: string;
  expiration: number;
  basemap?: ArcGISBasemap;
}

interface SessionRequest {
  mapType: string;
  language: string;
  region: string;
  layerTypes?: string[];
}

const sessions: CachedSessions[] = [];
let cachedAttributionBuilder: { sessionId: string; builder: GoogleAttribution } | null = null;

/**
 * GoogleBasemapBuilder is responsible for obtaining and caching session tokens
 * for each of the basemaps we use through the
 * [Map Tiles API](https://developers.google.com/maps/documentation/tile).
 */
const GoogleBasemapBuilder = (googleApiKey: string, reset?: boolean): GoogleBasemap => {
  if (reset) {
    // This is mainly for testing. It resets the cached sessions.
    sessions.length = 0;
    cachedAttributionBuilder = null;
  }

  const getAttribution = async (mapView: MapView, sessionId: string): Promise<string> => {
    if (cachedAttributionBuilder?.sessionId !== sessionId) {
      cachedAttributionBuilder = {
        sessionId,
        builder: GoogleAttributionBuilder(googleApiKey, sessionId),
      };
    }

    const attribution = await cachedAttributionBuilder.builder.getAttribution(
      mapView.zoom,
      mapView.extent,
    );
    return attribution;
  };

  const getCachedSessionByType = (mapType: string) => sessions.find((s) => s.type === mapType);

  const getSession = async (basemapId: string) => {
    const time = new Date().getTime() / 1000;
    // Acceptable mapTypes can be found here: https://developers.google.com/maps/documentation/tile/session_tokens
    // The values sent in here come from `/src/layer-picker/basemaps.ts`
    const existingSession = getCachedSessionByType(basemapId);
    const isTokenValid = existingSession && existingSession.expiration > time;

    if (isTokenValid) {
      return existingSession;
    }

    const body: SessionRequest = {
      mapType: basemapId === 'google-hybrid' ? 'satellite' : basemapId.replace('google-', ''),
      language: 'en-US',
      region: 'US',
    };

    // Terrain and hybrid require "layerTypes": https://developers.google.com/maps/documentation/tile/terrain
    if (basemapId === 'google-terrain' || basemapId === 'google-hybrid') {
      body.layerTypes = ['layerRoadmap'];
    }

    const response = await fetch(
      `https://tile.googleapis.com/v1/createSession?key=${googleApiKey}`,
      {
        method: 'post',
        body: JSON.stringify(body),
      },
    );
    const newSession = await response.json();
    const { session: id, expiry } = newSession;
    sessions.push({
      type: basemapId,
      id,
      expiration: expiry,
    });

    return getCachedSessionByType(basemapId);
  };

  const buildBasemap = async (basemapId: string, mapView: MapView) => {
    await mapView.when();
    const session = await getSession(basemapId);
    if (!session) {
      throw new Error('Unable to get session token');
    }
    const attribution = await getAttribution(mapView, session.id);

    if (session.basemap && !session.basemap.destroyed) {
      const layer = session.basemap.baseLayers.getItemAt(0) as WebTileLayer;
      if (layer) {
        layer.copyright = attribution;
      }
    } else {
      let urlTemplate = `https://tile.googleapis.com/v1/2dtiles/{z}/{x}/{y}?session=${session.id}&key=${googleApiKey}`;

      // Roadmap requires orientation: https://developers.google.com/maps/documentation/tile/roadmap
      if (session.type === 'google-roadmap') {
        urlTemplate += '&orientation=0';
      }
      const webTileLayer = new WebTileLayer({
        urlTemplate,
        copyright: attribution, // See https://developers.google.com/maps/documentation/tile/policies#displaying-googles-data-attributions
      });

      session.basemap = new ArcGISBasemap({ baseLayers: [webTileLayer], id: basemapId });
    }

    return session.basemap;
  };

  return {
    buildBasemap,
  };
};

export default GoogleBasemapBuilder;
